import type { User } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { hasExistingProfile, isOnboardingCompleted, isLegacyUser } from './onboardingAccountState';
import { getPostAuthRoute as getPostAuthRoutePure } from './postAuthRoute.pure';

export type { UserRole } from './postAuthRoute.pure';
export { getPostAuthRoutePure as getPostAuthRoute };

/**
 * Lazy migration: set users/{uid}.role = 'creator' and userType = 'influencer'
 * when we can infer the user is a creator. Idempotent.
 */
export async function lazyMigrateToCreatorIfInferred(
  uid: string,
  authUser?: User | null,
): Promise<boolean> {
  if (!uid) return false;
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.exists() ? userSnap.data() : null;
  if (!userData) return false;
  const role = (userData.role as string) ?? userData.userType;
  if (role === 'creator' || role === 'influencer' || role === 'brand') return false;

  const influencersRef = doc(db, 'influencers', uid);
  const infSnap = await getDoc(influencersRef);
  const hasInfluencersDoc = infSnap.exists();

  const docRecord = userData as Record<string, unknown>;
  const completed = isOnboardingCompleted(docRecord);
  const legacy = isLegacyUser(authUser ?? null);
  // Never infer creator from influencers/{uid} alone for post–onboarding-v1 accounts: stale or partial
  // influencer docs must not skip /select-role. Legacy + influencers, or completed onboarding, still migrate.
  const canInferCreator =
    hasExistingProfile(docRecord) ||
    (legacy && completed) ||
    (legacy && hasInfluencersDoc) ||
    (completed && hasInfluencersDoc);

  if (!canInferCreator) {
    return false;
  }

  await setDoc(
    userRef,
    {
      role: 'creator',
      userType: 'influencer',
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  if (process.env.NODE_ENV === 'development') {
    let reason = 'unknown';
    if (hasExistingProfile(docRecord)) reason = 'existing profile fields';
    else if (legacy && completed) reason = 'legacy + onboardingCompleted';
    else if (legacy && hasInfluencersDoc) reason = 'legacy + influencers doc';
    else if (completed && hasInfluencersDoc) reason = 'onboardingCompleted + influencers doc';
    console.log('[role] Lazy migration: set role=creator (' + reason + '):', uid);
  }
  return true;
}

/**
 * Returns post-auth route after applying lazy migration when applicable.
 * Call this from Login/Signup after fetching user doc.
 */
export async function getPostAuthRouteWithLazyMigration(
  uid: string,
  userDoc: Record<string, unknown> | null | undefined,
  authUser: User | null | undefined,
): Promise<string> {
  const role = userDoc?.role ?? userDoc?.userType;
  if (role === 'creator' || role === 'influencer' || role === 'brand') {
    return getPostAuthRoutePure(userDoc ?? undefined, authUser ?? undefined);
  }
  const migrated = await lazyMigrateToCreatorIfInferred(uid, authUser ?? null);
  if (migrated) {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    const data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
    return getPostAuthRoutePure(data ?? undefined, authUser ?? undefined);
  }
  return getPostAuthRoutePure(userDoc ?? undefined, authUser ?? undefined);
}
